import React, { useState, useEffect } from "react";
import {
  Comment,
  CommentThreadProps,
} from "@shared/components/EditorJs/comments/interfaces";
import SingleComment from "@shared/components/EditorJs/comments/SingleComment";
import { useUser } from "@services/UserContext";

const CommentThread: React.FC<CommentThreadProps> = ({
  commentList,
  handleReply,
  handleCommentDelete,
  blockId,
}) => {
  const [commentText, setCommentText] = useState<{ [key: string]: string }>({});
  const { user } = useUser();

  const [replyInputVisible, setReplyInputVisible] = useState<{
    [key: string]: boolean;
  }>({});
  const [positions, setPositions] = useState<{
    [key: string]: { top: number };
  }>({});
  const [isVisible, setIsVisible] = useState(false);

  // const key = `n|${}`; // Assuming you want to highlight the first comment in the thread

  useEffect(() => {
    const newPositions: { [key: string]: { top: number } } = {};
    const blockElement = document.querySelector(`[data-id='${blockId}']`);

    if (blockElement) {
      newPositions[blockId] = {
        top: 20,
      };
    }

    setPositions(newPositions);
    setIsVisible(true);
  }, []);

  const removeHighlightFromBlock = (blockId: string, comment: Comment) => {
    const parentElement = document.querySelector(`[data-id="${blockId}"]`);
    if (!parentElement) return;

    const removeHighlight = (node: Node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const spanElements = (node as Element).querySelectorAll(
          "span[style*='background-color']"
        );
        spanElements.forEach((span) => {
          const parent = span.parentNode;
          if (parent) {
            parent.replaceChild(
              document.createTextNode(span.textContent || ""),
              span
            );
            parent.normalize(); // Merges adjacent text nodes
          }
        });
      }
    };
    if (comment.anchor.length > 1) {
      const anchors = comment.anchor;
      anchors.forEach((anchorId: string) => {
        const element = document.querySelector(`[data-id="${anchorId}"]`);
        if (!element) return;
        removeHighlight(element);
      });
    } else {
      removeHighlight(parentElement);
    }
  };

  const highlightTextInBlock = (blockId: string, comment: Comment) => {
    // multiple anchorId
    const start = parseInt(comment.anchor[0].split("|")[1]) || 0;
    const end = parseInt(comment.anchor[0].split("|")[2]) || 0;

    if (start == 0 && end == 0) {
      const anchors = comment.anchor;

      // Multiple anchorIds: highlight full content of each element
      anchors.forEach((anchorId: string) => {
        const element = document.querySelector(`[data-id="${anchorId}"]`);
        if (!element) return;

        const wrapNodeContent = (node: Node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const span = document.createElement("span");
            span.style.backgroundColor = "yellow";
            span.textContent = node.textContent || "";
            (node as ChildNode).replaceWith(span);
          } else {
            node.childNodes.forEach(wrapNodeContent);
          }
        };

        element.childNodes.forEach(wrapNodeContent);
      });
    } else {
      // Single anchorId with offset-based highlighting
      const parentElement = document.querySelector(`[data-id="${blockId}"]`);
      if (!parentElement) return;

      let charCount = 0; // Tracks character index across nodes

      const highlightNode = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue || "";
          const textLength = text.length;

          if (charCount + textLength < start) {
            charCount += textLength; // Skip this node
            return;
          }

          const relativeStart = Math.max(start - charCount, 0);
          const relativeEnd = Math.min(end - charCount, textLength);

          if (relativeStart < relativeEnd) {
            const before = text.slice(0, relativeStart);
            const highlight = text.slice(relativeStart, relativeEnd);
            const after = text.slice(relativeEnd);

            const span = document.createElement("span");
            span.style.backgroundColor = "yellow";
            span.textContent = highlight;

            const fragment = document.createDocumentFragment();
            if (before) fragment.appendChild(document.createTextNode(before));
            fragment.appendChild(span);
            if (after) fragment.appendChild(document.createTextNode(after));

            (node as ChildNode).replaceWith(fragment);
          }

          charCount += textLength;
        } else {
          node.childNodes.forEach(highlightNode);
        }
      };

      parentElement.childNodes.forEach(highlightNode);
    }
  };

  return (
    <>
      {isVisible && (
        <div
          // className="comment rounded-xl w-96 overflow-y-auto max-h-[50vh] scrollbar-hide"
          className="comment bg-[#ffffff] shadow-lg border border-gray-300 p-4 pb-0  rounded-xl w-[26rem] max-h-[70vh] flex flex-col"
          style={{
            top: `${positions[blockId]?.top || 0}px`,
            right: 10,
            zIndex: 1,
            position: "absolute",
          }}
        >
          <h2 className="comment pb-2 text-[#98A2B3] border-b-2 border-[#e4e4e4]">
            Comments
          </h2>

          <div className="scrollbar-hide overflow-y-auto flex-1">
            {/* Loop through each offset (comment thread) inside the block */}
            {commentList.map((comment, threadIndex) => (
              <div
                key={threadIndex}
                className={`comment p-3 pl-0  bg-[#ffffff] ${
                  threadIndex != 0 ? "border-t-2" : ""
                } border-[#e4e4e4]`}
                onMouseEnter={() => highlightTextInBlock(blockId, comment)}
                onMouseLeave={() => removeHighlightFromBlock(blockId, comment)}
              >
                {/* First Comment in the Thread */}
                <SingleComment
                  comment={comment}
                  handleReply={handleReply}
                  handleCommentDelete={handleCommentDelete}
                  parentComment={true}
                  setReplyInputVisible={() =>
                    setReplyInputVisible({
                      ...replyInputVisible,
                      [comment.id]: true,
                    })
                  }
                />

                {/* Replies Section */}

                <div className="comment ml-10">
                  {comment.replies && comment.replies.length > 0 && (
                    <div>
                      {comment.replies.map((reply, index) => (
                        <SingleComment
                          key={index}
                          comment={reply}
                          handleReply={handleReply}
                          handleCommentDelete={handleCommentDelete}
                        />
                      ))}
                    </div>
                  )}
                </div>
                {/* Reply Input */}
                <div className="comment mt-2">
                  <div className="comment flex items-center gap-2 border-gray-300 rounded-lg bg-white">
                    <div className="comment bg-red-500 text-white w-8 h-8 flex items-center justify-center rounded-full font-bold">
                      {user?.first_name[0]}
                    </div>
                    <div className="items-center justify-center flex border-2 border-[#efefef] p-1 rounded-md flex-1">
                      <input
                        type="text"
                        className="comment border-none outline-none  px-2 py-1 rounded-md flex-1 w-full"
                        placeholder="Add a comment.."
                        value={commentText[comment.id] || ""}
                        onChange={(e) =>
                          setCommentText({
                            ...commentText,
                            [comment.id]: e.target.value,
                          })
                        }
                      />
                      <button
                        className="comment text-white rounded-lg cursor-pointer"
                        onClick={() => {
                          if ((commentText[comment.id] || "").trim() !== "") {
                            handleReply(
                              commentText[comment.id],
                              comment.id + "",
                              false
                            );
                            setReplyInputVisible({
                              ...replyInputVisible,
                              [comment.id]: false,
                            });
                            setCommentText({
                              ...commentText,
                              [comment.id]: "",
                            });
                          }
                        }}
                      >
                        <img
                          src="/send_icon.svg"
                          alt="Comment"
                          className="w-7 h-7"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default CommentThread;
